import { useState, useRef, useEffect, FormEvent } from 'react';
import Swal from 'sweetalert2';
import { useUploadKnowledgeDocMutation } from '../../../../../api/knowledgeApi';
import { useSuperGetOrganizationsQuery } from '../../../../../api/superAdminApi';
import { validateFile } from '../../../../../components/knowledge/KnowledgeUtils';

export function useKnowledgeUpload(propOrgId?: string, onSuccess?: () => void) {
  const [uploadDoc] = useUploadKnowledgeDocMutation();

  const [file, setFile] = useState<File | null>(null);
  const [fileError, setFileError] = useState<string | null>(null);
  const [title, setTitle] = useState('');
  const [desc, setDesc] = useState('');
  const [cat, setCat] = useState('');
  const [selectedOrgId, setSelectedOrgId] = useState(propOrgId || '');
  const [orgSearch, setOrgSearch] = useState('');
  const [page, setPage] = useState(1);
  const [allOrgs, setAllOrgs] = useState<any[]>([]);
  const [uploading, setUploading] = useState(false);
  const [drag, setDrag] = useState(false);
  const fileRef = useRef<HTMLInputElement>(null);

  const adminUserRaw = localStorage.getItem('admin_user');
  const adminUser = adminUserRaw ? JSON.parse(adminUserRaw) : null;
  const isSuperAdmin = adminUser?.role === 'SuperAdmin';

  const { data: orgData, isFetching } = useSuperGetOrganizationsQuery(
    { limit: 50, page, search: orgSearch },
    { skip: !isSuperAdmin }
  );

  useEffect(() => {
    setPage(1);
    setAllOrgs([]);
  }, [orgSearch]);

  useEffect(() => {
    if (orgData?.data?.data) {
      setAllOrgs(prev => {
        const newOrgs = [...prev];
        orgData.data.data.forEach((o: any) => {
          if (!newOrgs.find(existing => existing.id === o.id)) {
            newOrgs.push(o);
          }
        });
        return newOrgs;
      });
    }
  }, [orgData]);

  const handleScrollEnd = () => {
    if (orgData?.data?.pagination) {
      if (page < orgData.data.pagination.totalPages && !isFetching) {
        setPage(p => p + 1);
      }
    }
  };

  const orgOptions = allOrgs.map((org: any) => ({
    value: org.id,
    label: org.name || org.id
  }));

  const pickFile = (f: File) => {
    const err = validateFile(f);
    if (err) { setFileError(err); setFile(null); return; }
    setFileError(null); setFile(f);
    if (!title) setTitle(f.name.replace(/\.[^/.]+$/, ''));
  };

  useEffect(() => {
    if (isSuperAdmin && orgOptions.length > 0 && !selectedOrgId) {
      setSelectedOrgId(orgOptions[0].value);
    }
  }, [isSuperAdmin, orgOptions, selectedOrgId]);

  const handleUpload = async (e: FormEvent) => {
    e.preventDefault();
    if (!file || !title.trim()) return;
    setUploading(true);
    try {
      const fd = new FormData();
      fd.append('file', file); fd.append('title', title.trim());
      if (desc) fd.append('description', desc);
      if (cat) fd.append('category', cat);
      if (selectedOrgId) fd.append('organization_id', selectedOrgId);
      await uploadDoc({ formData: fd }).unwrap();
      Swal.fire({ title: 'Uploaded!', text: 'Document added to your knowledge base.', icon: 'success', confirmButtonColor: '#38bdf8', background: 'var(--admin-card-bg)', color: 'var(--admin-card-text)' });
      if (onSuccess) onSuccess();
    } catch (err: any) {
      Swal.fire({ title: 'Upload Failed', text: err?.data?.message || 'Could not upload document.', icon: 'error', confirmButtonColor: '#EF4444', background: 'var(--admin-card-bg)', color: 'var(--admin-card-text)' });
    } finally { setUploading(false); }
  };

  return {
    file,
    setFile,
    fileError,
    setFileError,
    title,
    setTitle,
    desc,
    setDesc,
    cat,
    setCat,
    selectedOrgId,
    setSelectedOrgId,
    orgSearch,
    setOrgSearch,
    uploading,
    drag,
    setDrag,
    fileRef,
    isSuperAdmin,
    isFetching,
    page,
    orgOptions,
    handleScrollEnd,
    pickFile,
    handleUpload
  };
}
